// src/components/AracDetay.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, onSnapshot, collection, query, where, orderBy } from 'firebase/firestore';
// Modern 2 sütunlu düzen için: Grid, Stack ve Paper
import { Box, Typography, Paper, Grid, CircularProgress, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Stack } from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'; // İkon

const getServicesSummary = (uygulamalar) => {
    if (typeof uygulamalar !== 'object' || uygulamalar === null) {
        return 'Hizmet bilgisi yok.';
    }
    const activeServices = [];
    if (Array.isArray(uygulamalar.camFilmi) && uygulamalar.camFilmi.length > 0) activeServices.push("Cam Filmi");
    if (Array.isArray(uygulamalar.ppf) && uygulamalar.ppf.length > 0) activeServices.push("PPF Kaplama");
    if (uygulamalar.yikama && uygulamalar.yikama.aktif) activeServices.push("Yıkama");
    if (Array.isArray(uygulamalar.diger) && uygulamalar.diger.filter(h => h && h.aciklama).length > 0) activeServices.push("Diğer");
    return activeServices.length > 0 ? activeServices.join(', ') : 'Belirtilmemiş';
};

// Araç logosunu getiren fonksiyon
const getLogoPath = (marka) => {
    if (!marka || typeof marka !== 'string' || marka.trim().length === 0) {
        return '/logos/default.svg';
    }
    const logoName = marka.split(' ')[0].toLowerCase().replace(' ', '-');
    return `/logos/${logoName}.svg`;
};


const AracDetay = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [vehicle, setVehicle] = useState(null);
    const [serviceHistory, setServiceHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const docRef = doc(db, "vehicles", id);
        const unsubscribeVehicle = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                setVehicle({ id: docSnap.id, ...docSnap.data() });
            } else {
                setLoading(false);
            }
        });

        const workOrdersQuery = query(
            collection(db, "workOrders"), 
            where("aracId", "==", id),
            orderBy("isEmriTarihi", "desc")
        );

        const unsubscribeWorkOrders = onSnapshot(workOrdersQuery, (snapshot) => {
            setServiceHistory(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        }, (error) => {
            console.error("Servis geçmişi sorgusu hatası (Muhtemelen index eksik): ", error);
            setLoading(false);
        });

        return () => {
            unsubscribeVehicle();
            unsubscribeWorkOrders();
        };
    }, [id]);

    if (loading) { return <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />; }
    if (!vehicle) { return <Typography variant="h5" align="center" sx={{mt: 4}}>Araç Bulunamadı.</Typography>; }

    // Modernize edilmiş 2 sütunlu JSX yapısı (DÜZELTİLMİŞ)
    return (
        <Box>
            <Typography variant="h4" gutterBottom>Araç Detayı: {vehicle.plaka}</Typography>
            
            <Grid container spacing={3}>
                {/* Sol Taraf: Araç Bilgi Kartı */}
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3, position: 'relative' }}>
                        
                        {/* Logo Alanı */}
                        <Box sx={{ 
                            width: 100, 
                            height: 100, 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            backgroundColor: '#f8f9fa', 
                            borderRadius: 2, 
                            p: 1,
                            mb: 2,
                            border: '1px solid #e0e0e0'
                        }}>
                            <Box 
                                component="img" 
                                src={getLogoPath(vehicle.marka)} 
                                onError={(e) => { e.currentTarget.src = '/logos/default.svg'; }} 
                                alt={vehicle.marka} 
                                sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} 
                            />
                        </Box>
                        
                        <Typography variant="h5" component="h1" gutterBottom>{vehicle.plaka}</Typography>
                        
                        {/* GÜNCELLENDİ: Eksik bilgiler (Marka, Model, Yıl) eklendi */}
                        <Stack spacing={1.5} sx={{mt: 2}}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <DirectionsCarIcon color="action" />
                                <Typography><b>{vehicle.marka} {vehicle.model}</b> ({vehicle.yil || '-'})</Typography>
                            </Box>
                            <Divider />
                            <Typography><b>Sahibi:</b> {vehicle.musteriAdSoyad}</Typography>
                            <Typography><b>Renk:</b> {vehicle.renk || '-'}</Typography>
                        </Stack>
                    </Paper>
                </Grid>

                {/* Sağ Taraf: Servis Geçmişi */}
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>Servis Geçmişi</Typography>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>İş Emri Tarihi</TableCell>
                                        <TableCell>Yapılan Hizmetler</TableCell>
                                        <TableCell>Durum</TableCell>
                                        <TableCell align="right">Tutar</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {serviceHistory.length > 0 ? serviceHistory.map(order => (
                                        <TableRow hover key={order.id} sx={{cursor: 'pointer'}} onClick={() => navigate(`/is-emri/${order.id}`)}>
                                            <TableCell>{order.isEmriTarihi?.toDate().toLocaleDateString('tr-TR') || '-'}</TableCell>
                                            <TableCell>{getServicesSummary(order.uygulamalar)}</TableCell>
                                            <TableCell>{order.durum}</TableCell>
                                            <TableCell align="right">{(order.toplamTutar ?? 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</TableCell>
                                        </TableRow>
                                    )) : (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center">Bu araca ait servis geçmişi bulunmamaktadır.</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default AracDetay;